import React from "react";
import {
  Chart,
  ChartSeries,
  ChartSeriesItem,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartValueAxis,
  ChartValueAxisItem,
  ChartTooltip,
  ChartLegend,
  SharedTooltipContext,
  ChartArea,
} from "@progress/kendo-react-charts";
import { IChartDataItemV2 } from "@iliotech/types";
import moment from "moment";
import cn from "classnames";
import s from "./PerformanceByAllocationChart.module.scss"; // Make sure to create this CSS module
import { useReferenceData } from "@iliotech/data-wire";

const BULLET_SIZE = 20;

type chartData = {
  [key: string]: IChartDataItemV2[];
};

interface PerformanceByAllocationChartProps {
  chartData: chartData;
  height?: number;
  width?: number;
}

export const PerformanceByAllocationChart: React.FC<
  PerformanceByAllocationChartProps
> = ({ chartData, height, width }) => {
  const { getAllocationLabel } = useReferenceData();
  const percentageValueFormatter = (v: number | undefined) =>
    v?.toFixed(2) + "%";

  const sharedTooltipRender = (context: SharedTooltipContext) => {
    const points = context.points
      ?.map((point) => ({
        label: point.series.name,
        color: point.series.color,
        value: point.value,
        series: point.series,
      }))
      .sort((a, b) => b.value - a.value);

    return (
      <div className={cn(s.tooltipSurround)}>
        <div style={{ marginLeft: `calc(${BULLET_SIZE}px + 1.25rem` }}>
          {moment(context.categoryText).format("DD MMM YYYY")}
        </div>
        <ul>
          {points?.map((point) => (
            <li key={point.label}>
              <div className={cn(s.row)}>
                <div
                  style={{
                    width: BULLET_SIZE,
                    height: BULLET_SIZE,
                    borderRadius: BULLET_SIZE / 2,
                    background: point.color,
                    marginRight: 5,
                  }}
                />
                <div className={cn(s.row, s.details)}>
                  <div className={cn(s.label)}>
                    {getAllocationLabel(point.label)}:&nbsp;&nbsp;
                  </div>
                  <div className={cn(s.value)}>
                    {percentageValueFormatter(point.value)}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <Chart style={{ height: height, width: width }}>
      <ChartArea height={height} width={width} background={"transparent"} />
      <ChartLegend visible={false} />
      <ChartTooltip
        opacity={0.9}
        background={"var(--dark)"}
        shared={true}
        render={sharedTooltipRender as any}
      />
      <ChartCategoryAxis>
        <ChartCategoryAxisItem
          baseUnit="days"
          type="date"
          maxDivisions={20}
          labels={{
            color: "var(--color)",
            rotation: -45,
            font: `12px "DM Sans", Nunito, sans-serif`,
            position: "start",
            content: (e) => moment(e.value).format(`DD MMM 'YY`),
          }}
        />
      </ChartCategoryAxis>
      <ChartValueAxis>
        <ChartValueAxisItem labels={{ format: "{0}%" }} />
      </ChartValueAxis>
      <ChartSeries>
        {Object.entries(chartData)?.map(([allocationName, allocationData]) => (
          <ChartSeriesItem
            key={allocationName}
            markers={{ visible: false }}
            type="line"
            data={allocationData || []}
            name={allocationName}
            field="value"
            categoryField="date"
            color={allocationData?.[0]?.color}
            visible={true}
          />
        ))}
      </ChartSeries>
    </Chart>
  );
};
